import React, { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Box, Button, Divider, Link, Typography } from "@mui/material";
import { useMandatorContext } from "@bn/theming";
import { SettingsLayout } from "@features/settings/components/SettingsLayout";
import { ConfirmDeleteDialog } from "@components";
import { resolvePortalUrl } from "@helpers";
import { useBankConnections } from "@features/settings/SettingsBankConnections/useBankConnections";
import InfoIcon from "@icons/myicons/info.svg?react";
import { useDeleteBankingApiConnectionV2 } from "@bn/api/apiv1";

type Props = {};

export const SettingsAdvancedPage = (props: Props) => {
  const { t } = useTranslation();
  const [
    isConfirmDeleteBankingApiConnectionModalOpen,
    setIsConfirmDeleteBankingApiConnectionModalOpen,
  ] = useState(false);
  const [
    confirmDeleteBankingApiConnectionError,
    setConfirmDeleteBankingApiConnectionError,
  ] = useState<string>();
  const {
    mutate: deleteBankingApiConnection,
    isPending: isBankingApiConnectionDeleting,
  } = useDeleteBankingApiConnectionV2();

  const { config } = useMandatorContext();
  const { finApiBankConnections, wealthApiBankConnections } =
    useBankConnections();
  const hasBankConnections = Boolean(
    [...finApiBankConnections, ...wealthApiBankConnections].length
  );

  const handleBankingApiConnectionDelete = () => {
    deleteBankingApiConnection(undefined, {
      onError: (err: AxiosApiError) => {
        setConfirmDeleteBankingApiConnectionError(err.response?.data.message);
      },
    });
  };

  return (
    <SettingsLayout
      breadcrumbs={[
        { text: t("settings.sidebar.title") },
        { text: t("settings.sidebar.common.advanced") },
      ]}
      title={t("settings.advanced.title")}
      subtitle={t("settings.advanced.subtitle")}
    >
      <Box>
        {hasBankConnections && (
          <Box mb={11}>
            <Typography variant="subtitle2" mb={11}>
              {t("settings.advanced.bankImport.title")}
            </Typography>
            <Typography variant="body4" mb={3}>
              {t("settings.advanced.deleteConnection.title")}
            </Typography>
            <Typography variant="body3" color="textSecondary" mb={5}>
              <Trans
                i18nKey="settings.advanced.deleteConnection.text"
                components={{
                  linkText: (
                    <Link
                      rel="norefferer"
                      href="https://www.finapi.io/"
                      target="_blank"
                    />
                  ),
                }}
              />
            </Typography>
            <Box
              display="flex"
              alignItems="center"
              gap={2}
              mb={5}
              color="primary.dark"
            >
              <InfoIcon width="20px" height="20px" />
              <Typography variant="body3">
                {t("settings.advanced.deleteConnection.info")}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              onClick={() =>
                setIsConfirmDeleteBankingApiConnectionModalOpen(true)
              }
            >
              {t("settings.advanced.deleteConnection.buttonText")}
            </Button>
          </Box>
        )}
      </Box>
      {config.deleteAccountUrl && (
        <Box>
          <Divider sx={{ mb: 11 }} />
          <Box mb={11}>
            <Typography variant="subtitle2" mb={3}>
              {t("settings.advanced.deletePortalAccount.title")}
            </Typography>
            {config.deleteAccountPortalName && (
              <Typography variant="body3" color="textSecondary" mb={5}>
                <Trans
                  i18nKey="settings.advanced.deletePortalAccount.note"
                  values={{ portalName: config.deleteAccountPortalName }}
                  components={{
                    bold: (
                      <Typography
                        component="span"
                        variant="body3"
                        color="primary.dark"
                      />
                    ),
                  }}
                />
              </Typography>
            )}
            <Button
              variant="contained"
              color="error"
              size="small"
              component={Link}
              href={resolvePortalUrl(config.deleteAccountUrl)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("settings.advanced.deletePortalAccount.buttonText")}
            </Button>
          </Box>
        </Box>
      )}
      <ConfirmDeleteDialog
        isOpen={isConfirmDeleteBankingApiConnectionModalOpen}
        title={t(
          "settings.advanced.deleteConnection.deleteConnectionConfirmModal.title"
        )}
        text={t(
          "settings.advanced.deleteConnection.deleteConnectionConfirmModal.text"
        )}
        cancelButtonText={t(
          "settings.advanced.deleteConnection.deleteConnectionConfirmModal.cancel"
        )}
        confirmButtonText={t(
          "settings.advanced.deleteConnection.deleteConnectionConfirmModal.confirm"
        )}
        onClose={() => setIsConfirmDeleteBankingApiConnectionModalOpen(false)}
        onDelete={handleBankingApiConnectionDelete}
        errorMessage={confirmDeleteBankingApiConnectionError}
        isDeleteRequestLoading={isBankingApiConnectionDeleting}
      />
    </SettingsLayout>
  );
};
